import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, Phone, Calendar, Users, Shield, Heart, TrendingUp, Star, FileText, DollarSign, CheckCircle, ClipboardCheck, CreditCard, UserCheck, Briefcase, Building, Building2, Network, MessageSquare, Bell } from 'lucide-react';

const MegaMenuFeatures = ({ isOpen, onMouseEnter, onMouseLeave }) => {
  const frontOfficeAgents = [
    { name: 'Amy, Receptionist', description: 'Handles inbound calls 24/7 with zero wait times', path: '/agents/receptionist', icon: Phone },
    { name: 'Leena, Schedule Optimizer', description: 'Prevents no-shows and fills cancellations', path: '/agents/schedule-optimizer', icon: Calendar },
    { name: 'Stephanie, Hygiene Recall', description: 'Books hygiene appointments and follow ups', path: '/agents/hygiene-recall', icon: Heart },
    { name: 'Amanda, Insurance Verification', description: 'Verifies eligibility and communicates copays', path: '/agents/insurance-verification', icon: Shield },
    { name: 'Kim, Case Acceptance', description: 'Helps patients understand treatment plans', path: '/agents/case-acceptance', icon: CheckCircle },
    { name: 'Morgan, Patient Intake', description: 'Collects forms and insurance cards before visits', path: '/agents/patient-intake', icon: ClipboardCheck },
    { name: 'Elise, Patient Experience', description: 'Follows up to ensure patient satisfaction', path: '/agents/patient-experience', icon: Star },
  ];

  const backOfficeAgents = [
    { name: 'Amanda, Insurance Verification', description: 'Automatically verifies patient insurance', path: '/agents/insurance-verification', icon: Shield },
    { name: 'Laura, Insurance Calls', description: 'Handles insurance-related phone inquiries', path: '/agents/insurance-calls', icon: Phone },
    { name: 'Ariel, Claim Submission', description: 'Generates and submits insurance claims', path: '/agents/claim-submission', icon: FileText },
    { name: 'Alayna, Claim Scrubbing', description: 'Reviews and corrects claims before submission', path: '/agents/claim-scrubbing', icon: ClipboardCheck },
    { name: 'Alisha, EOB Posting', description: 'Posts insurance payments automatically', path: '/agents/eob-posting', icon: DollarSign },
    { name: 'Sarah, Denial Management', description: 'Handles denied claims and appeals', path: '/agents/denial-management', icon: FileText },
    { name: 'Kianna, Patient Billing', description: 'Manages patient statements and billing', path: '/agents/patient-billing', icon: CreditCard },
    { name: 'Julia, AR & Collections', description: 'Tracks receivables and collections', path: '/agents/ar-collections', icon: DollarSign },
  ];

  return (
    <div 
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`fixed top-[72px] left-0 right-0 z-40 transition-all duration-300 ${
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
      }`}
    >
      <div className="flex justify-center px-6 pt-3 pb-3">
        <div className="w-full max-w-7xl rounded-2xl shadow-xl overflow-hidden border bg-white border-gray-200/80">
          <div className="grid grid-cols-2 gap-8 p-6">
            {[{ title: 'Front Office', agents: frontOfficeAgents }, { title: 'Back Office', agents: backOfficeAgents }].map(({ title, agents }) => (
              <div key={title}>
                <div className="text-sm font-semibold uppercase tracking-wider mb-3 text-blue-600">{title}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {agents.map((agent, index) => {
                    const IconComponent = agent.icon;
                    return (
                      <Link key={index} to={agent.path} className="block px-3 py-2.5 rounded-xl transition-all duration-150 group hover:bg-gray-50">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-blue-50 group-hover:bg-blue-100 transition-colors mt-0.5">
                            <IconComponent className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-900 group-hover:text-blue-600 transition-colors">{agent.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{agent.description}</div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t px-6 py-3 bg-gray-50 border-gray-100">
            <Link to="/#agents" className="text-sm font-semibold flex items-center space-x-2 group text-blue-600 hover:text-blue-700">
              <span>View all 14+ AI agents</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const DropdownMenu = ({ title, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);
  const handleMouseEnter = () => { clearTimeout(timeoutRef.current); setIsOpen(true); };
  const handleMouseLeave = () => { timeoutRef.current = setTimeout(() => setIsOpen(false), 100); };

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center space-x-1 transition-colors font-medium text-gray-600 hover:text-gray-900">
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 w-80 transition-all duration-200 ${
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
      }`}>
        <div className="rounded-2xl shadow-xl overflow-hidden border bg-white border-gray-200/80">
          {items.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <Link key={index} to={item.path} className={`block px-5 py-4 transition-all duration-150 group hover:bg-gray-50 ${index !== items.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <div className="flex items-start gap-3">
                  {IconComponent && (
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-blue-50 group-hover:bg-blue-100 transition-colors">
                      <IconComponent className="w-4 h-4 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900 group-hover:text-blue-600 transition-colors">{item.title}</div>
                    {item.description && <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>}
                  </div>
                  <ArrowRight className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFeaturesMouseEnter = () => { clearTimeout(timeoutRef.current); setFeaturesOpen(true); };
  const handleFeaturesMouseLeave = () => { timeoutRef.current = setTimeout(() => setFeaturesOpen(false), 100); };

  const solutionsItems = [
    { title: 'Front Office', description: 'AI solutions for patient-facing operations', path: '/solutions/front-office-suite', icon: Users },
    { title: 'Back Office', description: 'Automate billing, claims, and admin tasks', path: '/solutions/back-office-suite', icon: Briefcase },
    { title: 'Private Practices', description: 'Perfect for independent dental practices', path: '/solutions/private-practices', icon: Building },
    { title: 'Groups & DSOs', description: 'Enterprise solutions for multi-location practices', path: '/solutions/dsos', icon: Network },
  ];

  const commsSuiteItems = [
    { title: 'VoIP', description: 'Crystal clear voice calls', path: '/comms-suite/voip', icon: Phone },
    { title: 'eFax', description: 'Digital faxing solution', path: '/comms-suite/efax', icon: FileText },
    { title: '2-Way Messaging', description: 'Secure patient messaging', path: '/comms-suite/messaging', icon: MessageSquare },
    { title: 'Patient Reminders', description: 'Automated appointment reminders', path: '/comms-suite/reminders', icon: Bell },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] border-b border-gray-200/50'
        : 'bg-white/70 backdrop-blur-md'
    }`}>
      <nav className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2.5">
          <img src="/assets/alogo.png" alt="Asha Dental Logo" className="h-8 w-auto" />
          <span className="font-semibold text-lg text-gray-900">Asha Dental</span>
        </Link>

        <div className="hidden md:flex items-center space-x-1">
          <div className="relative px-3 py-2" onMouseEnter={handleFeaturesMouseEnter} onMouseLeave={handleFeaturesMouseLeave}>
            <button className="flex items-center space-x-1 transition-colors font-medium text-gray-600 hover:text-gray-900">
              <span>AI Agents</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${featuresOpen ? 'rotate-180' : ''}`} />
            </button>
            <MegaMenuFeatures isOpen={featuresOpen} onMouseEnter={handleFeaturesMouseEnter} onMouseLeave={handleFeaturesMouseLeave} />
          </div>
          <div className="px-3 py-2">
            <DropdownMenu title="Solutions" items={solutionsItems} />
          </div>
          <div className="px-3 py-2">
            <DropdownMenu title="Comms Suite" items={commsSuiteItems} />
          </div>
          <Link to="/migrations" className="px-3 py-2 transition-colors font-medium text-gray-600 hover:text-gray-900">
            Migration
          </Link>
        </div>

        <div className="flex items-center space-x-3">
          <Link to="/book-demo">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg">
              Request a Demo
            </Button>
          </Link>
        </div>
      </nav>
    </header>
  );
};

export default Header;
